// Acheter à nouveau - Page pour les ACHETEURS
// Connectée aux vraies APIs

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  RotateCcw, ShoppingCart, Package, ArrowLeft, Search, Filter,
  Star, Store, Clock, CheckCircle2, ChevronRight, Plus, Minus,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { purchaseHistoryAPI, cartAPI, authAPI } from '../../services/buyerAPI';

// Carte produit déjà acheté
function PreviouslyBoughtCard({ product, onAddToCart }) {
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  const handleQuantityChange = (delta) => {
    const newQty = Math.max(1, quantity + delta);
    setQuantity(newQty);
  };

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      await onAddToCart(product, quantity);
    } finally {
      setAdding(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all"
    >
      <div className="flex gap-4 p-4">
        {/* Image produit */}
        <div className="w-24 h-24 rounded-lg bg-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
          {product.product_image ? (
            <img src={product.product_image} alt={product.product_name} className="w-full h-full object-cover" />
          ) : (
            <Package size={32} className="text-gray-300" />
          )}
        </div>

        {/* Infos */}
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 line-clamp-2 mb-1">{product.product_name}</h3>
          
          <div className="flex items-center gap-2 text-sm mb-2">
            <Store size={14} className="text-gray-400" />
            <span className="text-gray-500">{product.seller_name}</span>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2 flex-wrap">
            <Clock size={12} />
            <span>Dernier achat le {formatDate(product.last_purchased_at)}</span>
            {product.purchase_count > 1 && (
              <>
                <span className="text-gray-300">•</span>
                <span>Commandé {product.purchase_count}x</span>
              </>
            )}
          </div>

          <p className="text-lg font-bold text-gray-900">{product.product_price?.toFixed(2)} €</p>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 pb-4 flex items-center gap-3">
        {/* Sélecteur de quantité */}
        <div className="flex items-center border border-gray-200 rounded-lg">
          <button
            onClick={() => handleQuantityChange(-1)}
            className="p-2 hover:bg-gray-50"
            disabled={quantity <= 1}
          >
            <Minus size={16} className="text-gray-500" />
          </button>
          <span className="px-4 py-2 font-medium text-gray-900">{quantity}</span>
          <button
            onClick={() => handleQuantityChange(1)}
            className="p-2 hover:bg-gray-50"
          >
            <Plus size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Bouton ajouter */}
        <Button
          onClick={handleAddToCart}
          disabled={adding}
          className="flex-1 bg-orange-500 hover:bg-orange-600 rounded-xl"
        >
          {adding ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
          ) : (
            <ShoppingCart size={16} className="mr-2" />
          )}
          Ajouter au panier
        </Button>
      </div>
    </motion.div>
  );
}

export default function AcheterANouveauPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      toast.error('Connecte-toi pour voir ton historique');
      navigate('/connexion');
      return;
    }
    fetchProducts();
  }, [navigate]);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await purchaseHistoryAPI.getAll(50);
      setProducts(data);
    } catch (e) {
      console.error('Error fetching purchase history:', e);
      setError(e.message);
      if (e.message.includes('401') || e.message.includes('authentifié')) {
        toast.error('Session expirée, reconnecte-toi');
        navigate('/connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product, quantity) => {
    try {
      await cartAPI.addItem({
        id: product.product_id,
        name: product.product_name,
        price: product.product_price,
        image: product.product_image,
        seller_id: product.seller_id,
        seller_name: product.seller_name
      }, quantity);
      toast.success(`${quantity}x ${product.product_name} ajouté au panier`);
    } catch (e) {
      toast.error('Erreur lors de l\'ajout au panier');
    }
  };

  const handleAddAllToCart = async () => {
    let added = 0;
    for (const product of filteredProducts) {
      try {
        await cartAPI.addItem({
          id: product.product_id,
          name: product.product_name,
          price: product.product_price,
          image: product.product_image,
          seller_id: product.seller_id,
          seller_name: product.seller_name
        }, 1);
        added++;
      } catch (e) {
        console.error('Error adding to cart:', e);
      }
    }
    if (added > 0) {
      toast.success(`${added} article(s) ajouté(s) au panier`);
    } else {
      toast.error('Erreur lors de l\'ajout au panier');
    }
  };

  // Filtrage et tri
  const filteredProducts = products
    .filter(p => 
      searchQuery === '' || 
      (p.product_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.seller_name || '').toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'recent') {
        return new Date(b.last_purchased_at) - new Date(a.last_purchased_at);
      } else if (sortBy === 'frequent') {
        return (b.purchase_count || 0) - (a.purchase_count || 0);
      } else if (sortBy === 'price-asc') {
        return (a.product_price || 0) - (b.product_price || 0);
      } else if (sortBy === 'price-desc') {
        return (b.product_price || 0) - (a.product_price || 0);
      }
      return 0;
    });

  // Stats
  const totalProducts = products.length;
  const totalOrders = products.reduce((sum, p) => sum + (p.purchase_count || 1), 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="acheter-a-nouveau-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg lg:hidden">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <RotateCcw size={24} className="text-indigo-600" />
                Acheter à nouveau
              </h1>
              <p className="text-sm text-gray-500">
                {totalProducts} produits • {totalOrders} commandes passées
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={20} />
            <span className="text-red-700">{error}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchProducts}
              className="ml-auto text-red-600 hover:text-red-700"
            >
              Réessayer
            </Button>
          </div>
        )}

        {/* Filtres */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Rechercher un produit déjà acheté..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm"
            >
              <option value="recent">Plus récents</option>
              <option value="frequent">Plus commandés</option>
              <option value="price-asc">Prix croissant</option>
              <option value="price-desc">Prix décroissant</option>
            </select>
          </div>
        </div>

        {/* Info box */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <CheckCircle2 size={20} className="text-indigo-600 flex-shrink-0" />
          <p className="text-sm text-indigo-700">
            Retrouve ici tous les produits que tu as déjà commandés. Clique pour les ajouter rapidement au panier !
          </p>
        </div>

        {/* Liste des produits */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
            <RotateCcw size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-bold text-gray-900 mb-2">Aucun produit trouvé</h3>
            <p className="text-gray-500 mb-6">
              {searchQuery 
                ? "Aucun produit ne correspond à ta recherche"
                : "Vous n'avez pas encore passé de commande"}
            </p>
            <Button 
              onClick={() => navigate('/produits')}
              className="bg-orange-500 hover:bg-orange-600 rounded-xl"
            >
              Découvrir nos produits
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts.map((product) => (
              <PreviouslyBoughtCard
                key={product.id || product.product_id}
                product={product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        )}

        {/* Bouton commander tous */}
        {filteredProducts.length > 0 && (
          <div className="mt-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl p-6 text-center text-white">
            <h3 className="text-xl font-bold mb-2">Recommander tes essentiels ?</h3>
            <p className="text-white/80 mb-4">
              Ajoute tous tes produits favoris au panier en un clic
            </p>
            <Button
              onClick={handleAddAllToCart}
              className="bg-white text-indigo-600 hover:bg-white/90 rounded-xl font-bold"
            >
              <ShoppingCart size={18} className="mr-2" />
              Tout ajouter au panier
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
